import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaInfoCircle, FaTrash, FaBell, FaFileAlt, FaDownload, FaDollarSign
} from 'react-icons/fa';
import { formatDateForDisplay, formatDateForInput, renderReminderTracking, formInputClass } from './menuTabUtils';

const ANNUAL_REPORT_STATES = ['Delaware', 'Wyoming', 'New Jersey', 'Florida', 'Pennsylvania', 'Washington'];

const RegisteredAgentTab = ({
  selectedMenuUser,
  handleRegisteredAgentDueDateUpdate,
  handleRegisteredAgentPaymentStatusUpdate,
  handleRegisteredAgentDocumentUpload,
  handleRegisteredAgentDocumentDelete,
}) => {
  const [registeredAgentState1, setRegisteredAgentState1] = useState('');
  const [registeredAgentDueDate1, setRegisteredAgentDueDate1] = useState('');
  const [registeredAgentState2, setRegisteredAgentState2] = useState('');
  const [registeredAgentDueDate2, setRegisteredAgentDueDate2] = useState('');
  const [registeredAgentUploading, setRegisteredAgentUploading] = useState(false);
  const [manualReminderLoading1, setManualReminderLoading1] = useState(false);
  const [manualReminderLoading2, setManualReminderLoading2] = useState(false);
  const [raPaymentHistory, setRaPaymentHistory] = useState([]);

  useEffect(() => {
    if (selectedMenuUser) {
      setRegisteredAgentState1(selectedMenuUser?.registeredAgentService?.state || '');
      setRegisteredAgentDueDate1(formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate));
      setRegisteredAgentState2(selectedMenuUser?.registeredAgentService?.state2 || '');
      setRegisteredAgentDueDate2(formatDateForInput(selectedMenuUser?.registeredAgentService?.dueDate2));

      if (selectedMenuUser._id) {
        const fetchRaPayments = async () => {
          try {
            const token = localStorage.getItem('employeeToken');
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/user-payments/${selectedMenuUser._id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
              const data = await res.json();
              setRaPaymentHistory((data || []).filter(p => p.service === 'Registered Agent'));
            }
          } catch (e) {
            console.error('Error fetching Registered Agent payment history:', e);
          }
        };
        fetchRaPayments();
      }
    }
  }, [selectedMenuUser]);

  const handleSendManualReminder = async (userId, slot = 1) => {
    const slotLabel = slot === 2 ? 'Service 2' : 'Service 1';
    if (!window.confirm(`Send a registered agent reminder email for ${slotLabel} to this client now?`)) return;
    if (slot === 2) setManualReminderLoading2(true);
    else setManualReminderLoading1(true);
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/send-immediate-registered-agent-reminder`,
        { userId, slot },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Reminder email sent successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder email.');
    } finally {
      if (slot === 2) setManualReminderLoading2(false);
      else setManualReminderLoading1(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h5 className="font-bold text-gray-900 dark:text-white text-base">Registered Agent Service – Due Date Setup</h5>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Manage state registrations and renewal due dates for this client (supports up to 2 states).</p>
      </div>

      {/* Registered Agent Service 1 */}
      <div className="p-4 bg-gray-50 dark:bg-neutral-800/60 rounded-2xl border border-gray-200 dark:border-neutral-700 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 flex items-center justify-center text-[11px] font-bold">1</span>
            Registered Agent Service 1
          </span>
          {selectedMenuUser?.registeredAgentService?.dueDate && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
              Active
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Select State</label>
            <select
              value={registeredAgentState1}
              onChange={(e) => setRegisteredAgentState1(e.target.value)}
              className={`${formInputClass} w-full`}
            >
              <option value="">-- Select State --</option>
              {ANNUAL_REPORT_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">Renewal Due Date</label>
              <FaInfoCircle className="w-3.5 h-3.5 text-primary-500" title="Automatic reminders: 1 month and 15 days before due date" />
            </div>
            <div className="flex gap-2">
              <input
                type="date"
                value={registeredAgentDueDate1}
                onChange={(e) => setRegisteredAgentDueDate1(e.target.value)}
                className={`${formInputClass} flex-1`}
              />
              <button
                type="button"
                onClick={() => {
                  if (!registeredAgentState1) {
                    alert('Please select a state for Registered Agent Service 1.');
                    return;
                  }
                  if (!registeredAgentDueDate1) {
                    alert('Please enter a renewal due date for Registered Agent Service 1.');
                    return;
                  }
                  handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, registeredAgentDueDate1, registeredAgentState1, 1);
                }}
                disabled={!registeredAgentState1 || !registeredAgentDueDate1}
                className="px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all active:scale-95 shadow-sm shrink-0"
              >
                Update
              </button>
              {selectedMenuUser?.registeredAgentService?.dueDate && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to remove Registered Agent Service 1?')) {
                      handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, null, '', 1);
                      setRegisteredAgentState1('');
                      setRegisteredAgentDueDate1('');
                    }
                  }}
                  className="px-3 py-2.5 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-600 hover:text-white border border-red-200 transition-all active:scale-95 shrink-0"
                  title="Remove Due Date"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedMenuUser?.registeredAgentService?.dueDate && (
          <div className="p-3.5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
            <div className="flex items-center justify-between mb-2">
              <h6 className="font-bold text-blue-900 dark:text-blue-300 text-xs">Current Registered Agent Due Date (Service 1)</h6>
              {selectedMenuUser.registeredAgentService.state && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-200/70 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                  {selectedMenuUser.registeredAgentService.state}
                </span>
              )}
            </div>
            <div className="p-2.5 bg-white dark:bg-neutral-900 rounded-lg flex items-center justify-between">
              <strong className="text-xs text-primary-600">Renewal Due Date:</strong>
              <span className="text-red-600 font-bold text-sm">{formatDateForDisplay(selectedMenuUser.registeredAgentService.dueDate)}</span>
            </div>
            <div className="mt-2.5">
              <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5">Automated Reminder Tracking</p>
              {renderReminderTracking(
                selectedMenuUser.registeredAgentService.oneMonthReminderSent,
                selectedMenuUser.registeredAgentService.fifteenDayReminderSent
              )}
            </div>
            <div className="mt-2.5 pt-2.5 border-t border-blue-100 dark:border-blue-800/30 flex items-center justify-between gap-3">
              <div>
                <span className="block text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400">Payment Status</span>
                <select
                  value={selectedMenuUser?.registeredAgentService?.paymentStatus || 'unpaid'}
                  onChange={(e) => {
                    if (handleRegisteredAgentPaymentStatusUpdate) {
                      handleRegisteredAgentPaymentStatusUpdate(selectedMenuUser._id, e.target.value, 1);
                    }
                  }}
                  className="mt-1 px-3 py-1.5 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg text-xs font-semibold text-gray-900 dark:text-white"
                >
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              {selectedMenuUser?.registeredAgentService?.paymentStatus === 'paid' && (
                <div className="text-right">
                  <span className="text-[10px] text-gray-400 block">Status</span>
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                    {selectedMenuUser?.registeredAgentService?.paidAt ? new Date(selectedMenuUser.registeredAgentService.paidAt).toLocaleDateString() : 'Paid'}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleSendManualReminder(selectedMenuUser._id, 1)}
              disabled={manualReminderLoading1}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white text-xs font-bold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-all"
            >
              {manualReminderLoading1 ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <FaBell className="w-3 h-3" />
                  Send Manual Reminder Now (Service 1)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Registered Agent Service 2 */}
      <div className="p-4 bg-gray-50 dark:bg-neutral-800/60 rounded-2xl border border-gray-200 dark:border-neutral-700 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 flex items-center gap-1.5">
            <span className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 flex items-center justify-center text-[11px] font-bold">2</span>
            Registered Agent Service 2
          </span>
          {selectedMenuUser?.registeredAgentService?.dueDate2 && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
              Active
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Select State</label>
            <select
              value={registeredAgentState2}
              onChange={(e) => setRegisteredAgentState2(e.target.value)}
              className={`${formInputClass} w-full`}
            >
              <option value="">-- Select State --</option>
              {ANNUAL_REPORT_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">Renewal Due Date</label>
              <FaInfoCircle className="w-3.5 h-3.5 text-primary-500" title="Automatic reminders: 1 month and 15 days before due date" />
            </div>
            <div className="flex gap-2">
              <input
                type="date"
                value={registeredAgentDueDate2}
                onChange={(e) => setRegisteredAgentDueDate2(e.target.value)}
                className={`${formInputClass} flex-1`}
              />
              <button
                type="button"
                onClick={() => {
                  if (!registeredAgentState2) {
                    alert('Please select a state for Registered Agent Service 2.');
                    return;
                  }
                  if (!registeredAgentDueDate2) {
                    alert('Please enter a renewal due date for Registered Agent Service 2.');
                    return;
                  }
                  handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, registeredAgentDueDate2, registeredAgentState2, 2);
                }}
                disabled={!registeredAgentState2 || !registeredAgentDueDate2}
                className="px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all active:scale-95 shadow-sm shrink-0"
              >
                Update
              </button>
              {selectedMenuUser?.registeredAgentService?.dueDate2 && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to remove Registered Agent Service 2?')) {
                      handleRegisteredAgentDueDateUpdate(selectedMenuUser._id, null, '', 2);
                      setRegisteredAgentState2('');
                      setRegisteredAgentDueDate2('');
                    }
                  }}
                  className="px-3 py-2.5 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-600 hover:text-white border border-red-200 transition-all active:scale-95 shrink-0"
                  title="Remove Due Date"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedMenuUser?.registeredAgentService?.dueDate2 && (
          <div className="p-3.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800/30">
            <div className="flex items-center justify-between mb-2">
              <h6 className="font-bold text-purple-900 dark:text-purple-300 text-xs">Current Registered Agent Due Date (Service 2)</h6>
              {selectedMenuUser.registeredAgentService.state2 && (
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-200/70 dark:bg-purple-800 text-purple-800 dark:text-purple-200">
                  {selectedMenuUser.registeredAgentService.state2}
                </span>
              )}
            </div>
            <div className="p-2.5 bg-white dark:bg-neutral-900 rounded-lg flex items-center justify-between">
              <strong className="text-xs text-primary-600">Renewal Due Date:</strong>
              <span className="text-red-600 font-bold text-sm">{formatDateForDisplay(selectedMenuUser.registeredAgentService.dueDate2)}</span>
            </div>
            <div className="mt-2.5">
              <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5">Automated Reminder Tracking</p>
              {renderReminderTracking(
                selectedMenuUser.registeredAgentService.oneMonthReminderSent2,
                selectedMenuUser.registeredAgentService.fifteenDayReminderSent2
              )}
            </div>
            <div className="mt-2.5 pt-2.5 border-t border-purple-100 dark:border-purple-800/30 flex items-center justify-between gap-3">
              <div>
                <span className="block text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400">Payment Status</span>
                <select
                  value={selectedMenuUser?.registeredAgentService?.paymentStatus2 || 'unpaid'}
                  onChange={(e) => {
                    if (handleRegisteredAgentPaymentStatusUpdate) {
                      handleRegisteredAgentPaymentStatusUpdate(selectedMenuUser._id, e.target.value, 2);
                    }
                  }}
                  className="mt-1 px-3 py-1.5 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg text-xs font-semibold text-gray-900 dark:text-white"
                >
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              {selectedMenuUser?.registeredAgentService?.paymentStatus2 === 'paid' && (
                <div className="text-right">
                  <span className="text-[10px] text-gray-400 block">Status</span>
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                    {selectedMenuUser?.registeredAgentService?.paidAt2 ? new Date(selectedMenuUser.registeredAgentService.paidAt2).toLocaleDateString() : 'Paid'}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleSendManualReminder(selectedMenuUser._id, 2)}
              disabled={manualReminderLoading2}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white text-xs font-bold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-all"
            >
              {manualReminderLoading2 ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <FaBell className="w-3 h-3" />
                  Send Manual Reminder Now (Service 2)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Registered Agent Payment History */}
      <div className="p-4 bg-white dark:bg-neutral-800/60 rounded-2xl border border-gray-200 dark:border-neutral-700 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h6 className="font-bold text-gray-900 dark:text-white text-xs flex items-center gap-2">
            <FaDollarSign className="text-emerald-500" /> Registered Agent Payment History
          </h6>
          {raPaymentHistory.length > 0 && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
              {raPaymentHistory.length} Record{raPaymentHistory.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {raPaymentHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-neutral-700 text-gray-500 dark:text-gray-400 uppercase text-[10px]">
                <tr>
                  <th className="p-2">Date</th>
                  <th className="p-2">State / Service</th>
                  <th className="p-2">Amount</th>
                  <th className="p-2">Transaction ID</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                {raPaymentHistory.map((p, idx) => (
                  <tr key={p._id || idx} className="hover:bg-gray-50 dark:hover:bg-neutral-700/50">
                    <td className="p-2 whitespace-nowrap text-gray-900 dark:text-white font-medium">
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                        {p.serviceState || p.state || 'Registered Agent'}{p.slot ? ` (Service ${p.slot})` : ''}
                      </span>
                    </td>
                    <td className="p-2 whitespace-nowrap font-bold text-gray-900 dark:text-white">
                      {p.currencySymbol || '$'}{p.displayAmount || (p.paidAmount ? p.paidAmount.toFixed(2) : '125.00')}
                    </td>
                    <td className="p-2 whitespace-nowrap font-mono text-[11px] text-gray-600 dark:text-gray-300">
                      {p.paymentId || p.orderId || '—'}
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        Paid
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">No payment records found for Registered Agent.</p>
        )}
      </div>

      <div className="pt-4 border-t border-gray-100 dark:border-neutral-700 mt-4">
        <h6 className="font-bold text-gray-900 dark:text-white mb-3">Upload Registered Agent Documents</h6>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Upload documents visible to the user in their Registered Agent section.</p>
        <input
          type="file"
          multiple
          onChange={async (e) => {
            if (e.target.files && e.target.files.length > 0) {
              setRegisteredAgentUploading(true);
              try {
                await handleRegisteredAgentDocumentUpload(selectedMenuUser._id, e.target.files);
              } finally {
                setRegisteredAgentUploading(false);
              }
              e.target.value = '';
            }
          }}
          disabled={registeredAgentUploading}
          className="w-full px-4 py-3 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary-600 file:text-white file:font-semibold disabled:opacity-50"
        />
      </div>

      {selectedMenuUser?.registeredAgentDocuments?.length > 0 && (
        <div className="mt-6 border-t border-gray-100 dark:border-neutral-700 pt-4">
          <h6 className="font-bold text-gray-900 dark:text-white mb-3">Uploaded Documents</h6>
          <div className="space-y-2">
            {selectedMenuUser.registeredAgentDocuments.map((doc, idx) => (
              <div key={doc._id || idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-100 dark:border-neutral-700">
                <div className="flex items-center gap-3 min-w-0">
                  <FaFileAlt className="w-5 h-5 text-primary-500 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white block truncate">{doc.originalName}</span>
                    <span className="text-[10px] text-gray-500">{new Date(doc.date).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL}${doc.fileUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                    title="Download"
                  >
                    <FaDownload className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRegisteredAgentDocumentDelete(selectedMenuUser._id, doc._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisteredAgentTab;
